import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import { clearItems } from '@/modules/shared/utils/store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { CID } from '@/modules/shared/utils/store.types'

///////////////////////////////////////////////////////////////////////////////
// Utils
///////////////////////////////////////////////////////////////////////////////

// TODO move to shared module
const toArray = (map: Map<any, any>) => Array.from(map.keys()).map((key) => map.get(key))

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type ID = number | string
type UUID = string

type Document = {
  id: ID
  uuid: UUID
  attachment_original_file_name: string
  attachment_download_url: string
  attachment_view_url: string
  fileable: {
    id: ID
    name: string
    type: string
  }
  shareholder: {
    _cid: CID
    id: ID
    name: string
    type: string
  }
  name: string
  tax_year: number
  updated_at: string
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type DocumentMap = Map<CID, Document>

export const useDocumentStore = defineStore('tax/documentStore', () => {
  const route = useRoute()
  const notificationStore = useNotificationStore()
  const baseUrl = computed(() => `/v4/${route.params.slug}/tax/documents`)

  const items = ref<DocumentMap>(new Map())
  const documents = computed(() => toArray(items.value))

  const fetchDocuments = async (cid: string = 'group') => {
    clearItems(items)
    try {
      const response = await useFetch(`${baseUrl.value}?cid=${cid}`).get<Document[]>()
      for (const item of response) {
        items.value.set(item.uuid, item)
      }
    } catch {
      notificationStore.enqueue('error', 'Unable to retrieve documents')
    }
  }

  const destroy = async (uuid: UUID) => {
    try {
      await useFetch(`${baseUrl.value}/${uuid}`).delete()
      items.value.delete(uuid)
    } catch {
      notificationStore.enqueue('error', 'Unable to delete document')
    }
  }

  return {
    items,
    documents,

    fetchDocuments,
    destroy,
  }
})
